import { z } from "zod";
import type { BusinessMetadata } from "@/domain/business-metadata";
import type { DiscoveryAnswers, DiscoveryEstimate } from "@/domain/types";
import type { ExecutableDiscoveryModel } from "@/domain/discovery-model";
import {
  presentEstimate,
  resolveEstimateDisplay,
  type EstimateDisplay,
  type EstimatePresentation,
} from "./estimate-presentation";

const amountRange = z
  .object({ low: z.number().finite().nonnegative(), high: z.number().finite().nonnegative() })
  .strict()
  .refine(({ low, high }) => high >= low);
const localized = z.record(z.string(), z.string());
export const displayedEstimateSchema = z
  .object({
    version: z.literal(1),
    currency: z.string().regex(/^[A-Z]{3}$/),
    currencyPrecision: z.number().int().min(0).max(4),
    estimate: z
      .object({
        low: z.number().finite().nonnegative(),
        high: z.number().finite().nonnegative(),
        currency: z.string().regex(/^[A-Z]{3}$/),
        confidence: z.number().finite(),
        includes: z.array(localized),
      })
      .strict()
      .nullable(),
    display: z.enum(["range", "fixed", "from", "hidden", "quote-required", "pending"]),
    scope: z.enum(["complete", "partial", "unavailable"]),
    excluded: z.array(localized),
    tax: z
      .object({
        type: z.enum(["vat", "gst", "tax"]).nullable(),
        ratePercent: z.number().finite().nonnegative().nullable(),
      })
      .strict()
      .nullable(),
    taxAmount: amountRange.nullable(),
    total: amountRange.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const base = value.estimate;
    const visible = ["range", "fixed", "from"].includes(value.display);
    const taxed = base !== null && value.tax?.ratePercent != null;
    const close = (a: number, b: number) => Math.abs(a - b) < 0.000001;
    if (
      visible !== (base !== null) ||
      (base && (base.currency !== value.currency || base.high < base.low)) ||
      taxed !== (value.taxAmount !== null && value.total !== null) ||
      (!taxed && (value.taxAmount !== null || value.total !== null))
    ) {
      ctx.addIssue({ code: "custom", message: "Inconsistent displayed estimate" });
      return;
    }
    if (base && value.taxAmount && value.total && value.tax?.ratePercent != null) {
      for (const endpoint of ["low", "high"] as const) {
        if (
          !close(
            value.taxAmount[endpoint],
            currencyAmount((base[endpoint] * value.tax.ratePercent) / 100, value.currencyPrecision),
          ) ||
          !close(value.total[endpoint], base[endpoint] + value.taxAmount[endpoint])
        )
          ctx.addIssue({ code: "custom", message: "Inconsistent displayed tax or total" });
      }
    }
  });

/** Versioned, immutable Lead presentation. Precise engine results remain separate. */
export type DisplayedEstimate = z.infer<typeof displayedEstimateSchema>;

export function currencyPrecision(currency: string): number {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
    .maximumFractionDigits!;
}

function currencyAmount(value: number, precision: number): number {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON * value) * factor) / factor;
}

/** Pick each endpoint's tier before rounding; exact thresholds belong to the 500 tier. */
export function roundBaseRange(estimate: DiscoveryEstimate): DiscoveryEstimate {
  const increment = (value: number) => (value < 10_000 ? 100 : value <= 100_000 ? 500 : 5_000);
  return {
    ...estimate,
    low: Math.floor(estimate.low / increment(estimate.low)) * increment(estimate.low),
    high: Math.ceil(estimate.high / increment(estimate.high)) * increment(estimate.high),
  };
}

export function displayEstimate(
  precise: DiscoveryEstimate | null,
  display: EstimateDisplay,
  tax: BusinessMetadata["tax"],
  currency: string,
  scope: EstimatePresentation["scope"] = "complete",
  excluded: DisplayedEstimate["excluded"] = [],
): DisplayedEstimate {
  const estimate =
    precise && ["range", "fixed", "from"].includes(display) ? roundBaseRange(precise) : null;
  const precision = currencyPrecision(currency);
  const knownTax = estimate && tax?.ratePercent != null;
  const taxAmount = knownTax
    ? {
        low: currencyAmount((estimate.low * tax.ratePercent!) / 100, precision),
        high: currencyAmount((estimate.high * tax.ratePercent!) / 100, precision),
      }
    : null;
  const total =
    estimate && taxAmount
      ? {
          low: estimate.low + taxAmount.low,
          high: estimate.high + taxAmount.high,
        }
      : null;
  return {
    version: 1,
    currency,
    currencyPrecision: precision,
    estimate,
    display: display === "fixed" && estimate && estimate.low !== estimate.high ? "range" : display,
    scope,
    excluded,
    tax: estimate && tax ? { ...tax } : null,
    taxAmount,
    total,
  };
}

/** Same projection for intermediate/final screens, every runtime mode, and Lead creation. */
export function presentDisplayedEstimate(
  model: ExecutableDiscoveryModel,
  answers: DiscoveryAnswers,
  precise: DiscoveryEstimate | null,
): DisplayedEstimate {
  const presentation = presentEstimate(model, answers, precise);
  return displayEstimate(
    presentation.estimate,
    presentation.pending
      ? "pending"
      : (presentation.display ?? resolveEstimateDisplay(model, answers, presentation.estimate)),
    model.business.tax,
    model.business.currency,
    presentation.scope,
    presentation.excluded,
  );
}
